import { API, graphqlOperation } from "aws-amplify";
import { carrierUserByDriverCode } from "../queries";
import { updateCarrierUserReloadYardCarrier } from "../mutations";
import {
  customerByEmailMini,
  userByEmailMini,
} from "../reloadyard/custom-queries";
import {
  createCarrierUserReloadYardCarrierMini,
  updateCarrierUserReloadYardCarrierMini,
} from "./carrier-mutations";
import { carrierUserByEmail } from "./carrier-queries";
import { normalizeCarrierUserType } from "../../utils/carrierUserUtils";

// Must match the driver API's comparison (driverApi lib/people.js and
// identifyLookup.js): trim + lowercase.
export const normalizeDriverLastName = (s) =>
  String(s || "").trim().toLowerCase();

// Codes are 4 bare digits, but ones issued before the 'TD-' prefix was dropped
// are still on file in the old spelling and the GSI is exact-match — the gate
// resolves either form (driverApi lib/people.js driverCodeVariants), so a
// clash check that queries only one form misses half the collisions.
const driverCodeVariants = (code) =>
  /^\d{4}$/.test(code) ? [code, `TD-${code}`] : [code];

// Driver codes are unique per (driverCode, lastName) pair, not globally —
// renaming a driver can collide with another driver holding the same code,
// which would lock BOTH out of code lookup at the gate (uniform NOT_FOUND).
export const findDriverCodeClash = async (record, newLastName) => {
  const wanted = normalizeDriverLastName(newLastName);
  for (const driverCode of driverCodeVariants(record.driverCode)) {
    let nextToken;
    do {
      const response = await API.graphql(
        graphqlOperation(carrierUserByDriverCode, { driverCode, nextToken }),
      );
      const page = response.data.carrierUserByDriverCode;
      const clash = (page.items || []).find(
        (u) =>
          u && u.id !== record.id && normalizeDriverLastName(u.lastName) === wanted,
      );
      if (clash) return clash;
      nextToken = page.nextToken;
    } while (nextToken);
  }
  return null;
};

// performDeleteAccount - call graphQl, no try-catch
// handleCreateAccount - with try-catch
// processUpdateAccount - process for lambda

// Strips form-only and relational fields before a createCarrierUser or updateCarrierUser mutation.
// CarrierUser schema fields: id, status, cognitoId, email, firstName, lastName, phone
export const sanitizeCarrierUser = (carrierUser) => {
  const sanitized = { ...carrierUser };
  [
    "acl",
    "attachments",
    "businessName",
    "carrierId",
    "createdAt",
    "notes",
    "reloadYardCarriers",
    "reloadYardIds",
    "reloadYards",
    "type",
    "updatedAt",
  ].forEach((key) => delete sanitized[key]);
  return sanitized;
};

// Members of the reloadYardCarrierByActiveByTypeByName composite sort key. An
// update touching any of them has to send all of them: the resolver composes
// the key from the input alone, rendering an absent member as the literal
// "${ctx.args.input.<field>}", which no tab prefix matches — the row silently
// leaves the list.
const KEY_MEMBER_FIELDS = ["active", "type", "businessName", "lastName"];

/**
 * Canonicalize the junction `type` on a write that touches the composite key.
 *
 * Most updates spread a stored row, and legacy rows carry null, the pre-rename
 * short forms ("DISPATCH"/"DRIVER") or "CARRIER_DISPATCH_ADMIN" — none of which
 * belong in the key.
 *
 * A write that touches the key but carries no `type` property at all is a
 * caller bug — a query that failed to select it — and is rejected instead of
 * defaulted: guessing dispatch there would move a driver into the dispatch tab
 * silently and permanently.
 *
 * @param {object} record - junction fields about to be written
 * @returns {object} the same record with a canonical `type`
 */
const withCanonicalType = (record) => {
  if (!("type" in record)) {
    throw new Error(
      "CarrierUserReloadYardCarrier write touches the type key without a `type` field — " +
        "select `type` in the query this record came from.",
    );
  }
  return { ...record, type: normalizeCarrierUserType(record.type) };
};

export const sanitizeUserReloadCarrier = (userReloadCarrier) => {
  const sanitized = { ...userReloadCarrier };
  ["carrier", "createdAt", "reloadYard", "updatedAt", "user"].forEach(
    (key) => delete sanitized[key],
  );
  if (KEY_MEMBER_FIELDS.some((key) => key in sanitized)) {
    return withCanonicalType(sanitized);
  }
  return sanitized;
};

export const sanitizeCarrier = (carrier) => {
  const sanitized = { ...carrier };
  // companyId is admin-write-only at the field level (see schema): the stamp is
  // handleCarrierChange's to place, and a save that carries it back — even
  // unchanged, which is what spreading a fetched record does — is refused whole.
  ["companyId", "createdAt", "updatedAt", "users"].forEach(
    (key) => delete sanitized[key],
  );
  return sanitized;
};

// mutations.updateCarrierUserReloadYardCarrier
export const performUpdateUserReloadCarrier = async (userReloadCarrier) => {
  const userReloadCarrierInput = sanitizeUserReloadCarrier(userReloadCarrier);
  return API.graphql(
    graphqlOperation(updateCarrierUserReloadYardCarrier, {
      input: userReloadCarrierInput,
    }),
  );
};

// carrier-mutations.updateCarrierUserReloadYardCarrierMini
export const performUpdateUserReloadCarrierMini = async (userReloadCarrier) => {
  const userReloadCarrierInput = sanitizeUserReloadCarrier(userReloadCarrier);
  return API.graphql(
    graphqlOperation(updateCarrierUserReloadYardCarrierMini, {
      input: userReloadCarrierInput,
    }),
  );
};

export const performCreateUserReloadCarrier = async (
  user,
  carrier,
  userYardCarrier,
) => {
  const toCreateRecord = {
    active: "true",
    groups: window.appUser.reloadYard.groups,
    carrierUserId: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    phone: user.phone,
    reloadYardId: window.appUser.reloadYardId,
    carrierId: carrier.id,
    businessName: carrier.businessName,
    // Composite-key member (see withCanonicalType) — a caller that omits it
    // would create a row invisible to the tabbed list's GSI. Spelled out so an
    // omitting caller gets the documented dispatch default rather than the
    // missing-field error an update would raise.
    type: null,
    ...userYardCarrier,
  };

  await API.graphql(
    graphqlOperation(createCarrierUserReloadYardCarrierMini, {
      input: withCanonicalType(toCreateRecord),
    }),
  );
};

/**
 * Write one junction row exactly as given.
 *
 * performCreateUserReloadCarrier above assembles the row around the signed-in
 * user's own yard, which is the only yard a manager can invite into. A carrier
 * dispatch user invites into several at once, each with its own yard, carrier
 * and groups, so those callers assemble the rows themselves.
 *
 * The `type` is still canonicalized here rather than trusted: these callers
 * assemble rows by hand, and a hand-assembled row carrying a legacy spelling
 * would sort outside its own tab.
 *
 * @param {object} input - a complete CarrierUserReloadYardCarrier input
 */
export const performCreateCarrierUserJunction = async (input) =>
  API.graphql(
    graphqlOperation(createCarrierUserReloadYardCarrierMini, {
      input: withCanonicalType({ type: null, ...input }),
    }),
  );

/**
 * Whoever already holds an email address, across the three tables a sign-in can
 * come from.
 *
 * One address is one account on transloads.co, so every invite path has to ask
 * all three before minting a user: a yard's staff member, a customer contact and
 * a carrier's dispatcher cannot share an address, and the tables that hold them
 * have no constraint saying so.
 *
 * The carrier lookup uses the login query rather than the mini one: its
 * junctions carry the yard and carrier of each row, which is what tells an
 * invite whether the person is already a colleague at a given yard.
 *
 * @param {string} email
 * @returns {Promise<{carrierUser: object|undefined, customer: object|undefined,
 *   yardUser: object|undefined}>}
 */
export const findExistingUserByEmail = async (email) => {
  const [carrierResponse, customerResponse, userResponse] = await Promise.all([
    API.graphql(graphqlOperation(carrierUserByEmail, { email })),
    API.graphql(graphqlOperation(customerByEmailMini, { email })),
    API.graphql(graphqlOperation(userByEmailMini, { email })),
  ]);

  return {
    carrierUser: carrierResponse.data.carrierUserByEmail.items[0],
    customer: customerResponse.data.customerByEmail.items[0],
    yardUser: userResponse.data.userByEmail.items[0],
  };
};

export const setActiveUserReloadYardCarriers = (userReloadCarriers, active) => {
  return userReloadCarriers.map((userReloadCarrier) =>
    performUpdateUserReloadCarrierMini({ ...userReloadCarrier, active }),
  );
};
